/**
 * Town interaction flow (HEX-72).
 *
 * Healing now lives at the town tile only (ADR-0015 §6 economy rework) — shops
 * sell cards and no longer heal. The town modal offers the original fixed trade:
 * `TOWN_HEAL_PRICE` gold for `TOWN_HEAL_HP` HP, up to the shared player max HP,
 * repeatable until unaffordable or capped.
 *
 * DOM-overlay-above-Pixi, reusing the shared interaction-modal chrome (same as
 * ChestFlow / ShopFlow). Gold / HP mutations persist on close.
 */

import { PLAYER_MAX_HP } from '../combat/player';
import type { Character } from '../core/character';
import type { Tile } from '../core/tile';
import { getTownState } from '../lifecycle/townState';
import type { Persistence } from '../persistence/Persistence';

/** Fixed trade: spend 5 gold for 2 HP, up to maxHp. Price tuned 10→5 (HEX-81
 * heal rider); HP doubled 1→2 (HEX-89 HP economy), price unchanged. Relocated
 * from the shop's heal tab in HEX-72 (was `SHOP_HEAL_PRICE` / `SHOP_HEAL_HP`). */
export const TOWN_HEAL_PRICE = 5;
export const TOWN_HEAL_HP = 2;

/** Cap is the shared player max HP (same pin as CharacterFlow.SPAWN_HP). */
const HEAL_CAP = PLAYER_MAX_HP;

export interface TownFlowDeps {
  readonly persistence: Pick<Persistence, 'saveCharacter'>;
}

export interface TownFlowStartOptions {
  readonly character: Character;
  readonly tile: Tile;
  readonly deps: TownFlowDeps;
  readonly onResolved: () => void | Promise<void>;
}

export class TownFlow {
  private modal: HTMLDivElement | null = null;
  private goldLine: HTMLSpanElement | null = null;
  private active = false;

  constructor(private readonly container: HTMLElement) {}

  /**
   * Mount the town modal if the tile is a town WHOSE BOSS IS BEATEN. An evil
   * (or under-threat) town offers no services — the boss-combat flow pre-empts it
   * (HEX-122 / ADR-0018 §4) — so the heal/rest modal opens only once the town is
   * `liberated`. Returns true when mounted.
   */
  start(opts: TownFlowStartOptions): boolean {
    if (this.active) return false;
    if (opts.tile.type !== 'town') return false;
    if (getTownState(opts.tile) !== 'liberated') return false;
    this.active = true;
    this.modal = this.mountModal(opts);
    return true;
  }

  isActive(): boolean {
    return this.active;
  }

  destroy(): void {
    this.modal?.remove();
    this.modal = null;
    this.goldLine = null;
    this.active = false;
  }

  private mountModal(opts: TownFlowStartOptions): HTMLDivElement {
    const root = document.createElement('div');
    root.className = 'hex6-interaction-modal';
    root.dataset.role = 'town-modal';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-label', 'Town');

    const panel = document.createElement('div');
    panel.className = 'hex6-interaction-modal__panel';

    const title = document.createElement('h2');
    title.className = 'hex6-interaction-modal__title';
    title.textContent = 'Town';
    panel.appendChild(title);

    const goldRow = document.createElement('div');
    goldRow.className = 'hex6-interaction-modal__row';
    const goldLabel = document.createElement('span');
    goldLabel.textContent = 'Gold';
    this.goldLine = document.createElement('span');
    this.goldLine.dataset.role = 'town-gold';
    goldRow.append(goldLabel, this.goldLine);
    panel.appendChild(goldRow);

    const character = opts.character;

    const hpRow = document.createElement('div');
    hpRow.className = 'hex6-interaction-modal__row';
    const hpLabel = document.createElement('span');
    hpLabel.textContent = 'HP';
    const hpLine = document.createElement('span');
    hpLine.dataset.role = 'town-hp';
    hpRow.append(hpLabel, hpLine);
    panel.appendChild(hpRow);

    const offer = document.createElement('div');
    offer.dataset.role = 'town-offer';
    offer.textContent = `Heal +${TOWN_HEAL_HP} HP for ${TOWN_HEAL_PRICE} gold`;
    panel.appendChild(offer);

    const actions = document.createElement('div');
    actions.className = 'hex6-interaction-modal__actions';

    const buy = document.createElement('button');
    buy.type = 'button';
    buy.dataset.action = 'town-heal';
    buy.className = 'hex6-interaction-modal__button hex6-interaction-modal__button--primary';
    buy.textContent = 'Heal';

    // Update in place across repeated purchases so the button node persists.
    const sync = () => {
      hpLine.textContent = `${character.hp} / ${HEAL_CAP}`;
      buy.disabled = character.gold < TOWN_HEAL_PRICE || character.hp >= HEAL_CAP;
      if (this.goldLine) this.goldLine.textContent = String(character.gold);
    };
    buy.addEventListener('click', () => {
      if (character.gold < TOWN_HEAL_PRICE || character.hp >= HEAL_CAP) return;
      character.gold -= TOWN_HEAL_PRICE;
      character.hp = Math.min(HEAL_CAP, character.hp + TOWN_HEAL_HP);
      sync();
    });

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.dataset.action = 'town-close';
    closeBtn.className = 'hex6-interaction-modal__button';
    closeBtn.textContent = 'Close';
    closeBtn.addEventListener('click', () => {
      void this.close(opts);
    });

    actions.append(buy, closeBtn);
    panel.appendChild(actions);

    root.appendChild(panel);
    this.container.appendChild(root);
    sync();
    return root;
  }

  private async close(opts: TownFlowStartOptions): Promise<void> {
    if (!this.active) return;
    this.destroy();
    await opts.deps.persistence.saveCharacter(opts.character);
    await opts.onResolved();
  }
}
